import React, { useState } from "react";
import { Form, Input, Button, Typography, message, Modal } from "antd";
import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
import styled from "styled-components";
import { motion } from "framer-motion";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";

import { auth, db } from "../firebaseConfig";
import lemonLogo from "../assets/logo.png";

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */

/**
 * 1) Container теперь занимает всю ширину (100vw) и высоту (100vh) экрана.
 * 2) Всё внутри идёт в колонку и центрируется по горизонтали.
 * 3) Паддинг по бокам, чтобы на «узких» экранах элементы не прилипали к самому краю.
 */
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100vw;      /* растянуть на всю ширину экрана */
  height: 100vh;     /* растянуть на всю высоту экрана */
  padding: 0 24px;   /* отступы слева/справа */
  background: #ffffff;
`;

/**
 * Лого: фиксированного размера, ниже мы его центрируем за счёт align-items.
 */
const Logo = styled(motion.img)`
  width: 88px;
  height: 88px;
  margin-top: 56px;
  margin-bottom: 24px;
`;

/**
 * 1) Форма растягивается на всю ширину контейнера (width: 100%).
 * 2) max-width убран, чтобы на мобильных не было «пустого пространства» по бокам.
 * 3) Инпуты внутри тоже на 100% ширины.
 */
const StyledForm = styled(Form)`
  width: 100%;          /* растягиваем под ширину экрана (с учётом padding) */
  /* max-width: 340px; */ /* можно вернуть, если нужен ограничитель на десктопе */

  .ant-form-item {
    margin-bottom: 20px;
  }

  .ant-input,
  .ant-input-password {
    width: 100%;        /* инпуты во всю ширину */
    background: #f6f6f6;
    border: none;
    border-radius: 12px;
    padding: 14px 16px;
    font-size: 15px;
    box-sizing: border-box;
  }
`;

/**
 * Кнопка регистрации — 100% ширины формы.
 */
const PrimaryButton = styled(Button)`
  width: 100%;
  height: 48px;
  border-radius: 24px !important;
  border: none;
  background: #ffd233;
  color: #000;
  font-weight: 600;
  letter-spacing: 0.5px;
  transition: transform 0.2s ease;

  &:hover,
  &:focus {
    background: #ffcc00;
    color: #000;
    border: none;
  }

  &:active {
    transform: scale(0.96);
  }
`;

/**
 * Ссылочная кнопка «Уже есть аккаунт? Залогиниться» — тоже на всю ширину
 * и центрированная.
 */
const LinkButton = styled(Button)`
  display: block;
  width: 100%;
  padding: 0;
  height: auto;
  border: none;
  background: transparent;
  color: #ffd233 !important;
  text-align: center;

  &:hover,
  &:focus {
    color: #ffcc00 !important;
    text-decoration: none;
  }
`;

/**
 * Нижний блок «Есть аккаунт? Залогиниться» — в колонку, центр.
 */
const BottomBlock = styled.div`
  margin-top: auto;
  margin-bottom: 32px;
  display: flex;
  flex-direction: column;  /* вертикально, чтобы занимать всю ширину */
  align-items: center;
  width: 100%;
  gap: 8px;
`;

/**
 * Стили для message
 */
const MessageStyles = styled.div`
  .ant-message {
    top: 24px;
  }
  
  .ant-message-notice-content {
    padding: 12px 16px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
  
  .ant-message-success {
    background: #f6ffed;
    border: 1px solid #b7eb8f;
  }
  
  .ant-message-error {
    background: #fff2f0;
    border: 1px solid #ffccc7;
  }
  
  .ant-message-warning {
    background: #fffbe6;
    border: 1px solid #ffe58f;
  }
`;

/* -------------------------------------------------------------------------- */
/*                                   Component                                */
/* -------------------------------------------------------------------------- */

const { Title, Text } = Typography;

const passwordCriteria = {
  lower: /(?=.*[a-z])/,
  upper: /(?=.*[A-Z])/,
  number: /(?=.*[0-9])/,
  special: /(?=.*[@$!%*?&])/,
  length: /(?=.{8,})/,
};

const isPasswordValid = (pwd) =>
  Object.values(passwordCriteria).every((regexp) => regexp.test(pwd));

const Register = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  // Konfiguracja message
  React.useEffect(() => {
    message.config({
      top: 24,
      duration: 3,
      maxCount: 3,
      rtl: false,
    });
  }, []);

  // Funkcja dla polskich komunikatów błędów Firebase
  const getPolishErrorMessage = (errorCode) => {
    const errorMessages = {
      'auth/email-already-in-use': 'Ten adres e-mail jest już używany. Zaloguj się lub użyj innego adresu.',
      'auth/invalid-email': 'Nieprawidłowy format adresu e-mail.',
      'auth/operation-not-allowed': 'Rejestracja za pomocą e-mail i hasła jest wyłączona.',
      'auth/weak-password': 'Hasło jest zbyt słabe. Użyj co najmniej 6 znaków.',
      'auth/network-request-failed': 'Błąd połączenia z siecią. Sprawdź swoje połączenie internetowe.',
      'auth/too-many-requests': 'Zbyt wiele prób. Spróbuj ponownie później.',
    };
    
    return errorMessages[errorCode] || 'Wystąpił nieoczekiwany błąd podczas rejestracji. Spróbuj ponownie.';
  };

  const handleRegister = async ({ email, password, confirmPassword }) => {
    // Walidacja hasła
    if (!isPasswordValid(password)) {
      messageApi.warning({
        content: 'Hasło nie spełnia wymagań bezpieczeństwa. Musi zawierać: min. 8 znaków, 1 dużą literę, 1 cyfrę i 1 znak specjalny.',
        duration: 5,
      });
      return;
    }
    
    if (password !== confirmPassword) {
      messageApi.error({
        content: 'Hasła nie są identyczne. Sprawdź i spróbuj ponownie.',
        duration: 4,
      });
      return;
    }

    setLoading(true);
    const key = 'register';
    
    // Pokazujemy loading message
    messageApi.open({
      key,
      type: 'loading',
      content: 'Rejestracja w toku...',
      duration: 0,
    });

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const { user } = userCredential;

      await setDoc(doc(db, "users", user.uid), {
        email,
        registeredAt: new Date(),
        role: "user",
        status: "Wymagana weryfikacja KYC",
      });

      // Pokazujemy sukces
      messageApi.open({
        key,
        type: 'success',
        content: 'Rejestracja zakończona pomyślnie! Przekierowywanie do logowania...',
        duration: 3,
      });
      
      setTimeout(() => {
        navigate("/login");
      }, 1500);
      
    } catch (err) {
      console.error("Błąd rejestracji:", err);
      console.error("Kod błędu:", err.code);
      
      // Pobieramy odpowiednie polskie komunikaty błędów
      const errorMessage = getPolishErrorMessage(err.code);
      
      // Pokazujemy błąd
      messageApi.open({
        key,
        type: 'error',
        content: errorMessage,
        duration: 5,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <MessageStyles>
      {contextHolder}
      <Container>
        <Logo
          src={lemonLogo}
          alt="Logo Spario"
          initial={{ scale: 0.5, opacity: 0 }}
          animate={loading ? { rotate: 360 } : { scale: 1, opacity: 1 }}
          transition={
            loading
              ? { duration: 1, ease: "linear", repeat: Infinity }
              : { duration: 0.6, ease: "easeOut" }
          }
        />

        {/* Заголовок и описание растягиваются на 100% и центрируются */}
        <Title level={4} style={{ marginBottom: 4, textAlign: "center", width: "100%" }}>
          Utwórz konto Spario
        </Title>
        <Text
          type="secondary"
          style={{ marginBottom: 32, textAlign: "center", width: "100%" }}
        >
          Wypełnij poniższe pola, aby rozpocząć
        </Text>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          style={{ width: "100%" }}
        >
          <StyledForm
            form={form}
            layout="vertical"
            name="register"
            onFinish={handleRegister}
            requiredMark={false}
          >
            <Form.Item
              label="Adres e-mail"
              name="email"
              rules={[
                { required: true, message: "Wprowadź adres e-mail" },
                { type: "email", message: "Nieprawidłowy adres e-mail" },
              ]}
            >
              <Input placeholder="you@example.com" size="large" disabled={loading} />
            </Form.Item>

            <Form.Item
              label="Hasło"
              name="password"
              rules={[{ required: true, message: "Wprowadź hasło" }]}
              tooltip="Min. 8 znaków, 1 duża litera, 1 cyfra, 1 znak specjalny (@$!%*?&)"
            >
              <Input.Password
                placeholder="Min. 8 znaków"
                size="large"
                disabled={loading}
                iconRender={(vis) =>
                  vis ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                }
              />
            </Form.Item>

            <Form.Item
              label="Potwierdź hasło"
              name="confirmPassword"
              rules={[{ required: true, message: "Potwierdź hasło" }]}
            >
              <Input.Password
                placeholder="Powtórz hasło"
                size="large"
                disabled={loading}
                iconRender={(vis) =>
                  vis ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                }
              />
            </Form.Item>

            <Form.Item>
              <PrimaryButton type="primary" htmlType="submit" disabled={loading}>
                {loading ? "REJESTROWANIE..." : "ZAREJESTRUJ SIĘ"}
              </PrimaryButton>
            </Form.Item>
          </StyledForm>
        </motion.div>

        <BottomBlock>
          <Text>Masz już konto?</Text>
          <LinkButton type="link" onClick={() => navigate("/login")} disabled={loading}>
            Zaloguj się
          </LinkButton>
        </BottomBlock>
      </Container>
    </MessageStyles>
  );
};

export default Register;